import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { apiFetch } from "../../data/session";

const ROLE = { parent: "Parent", tutor: "Tuteur", teacher: "Enseignant", other: "Autre" };
const VIEWS = [["pending", "En attente"], ["approved", "Approuvées"], ["rejected", "Rejetées"]];
const MODES = [["invite", "Sur invitation (liste d’attente)"], ["open", "Ouvert (inscription publique)"]];
const pill = (active) =>
  `rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
    active ? "border-[#25D366] bg-[#25D366] text-[#0a2914]" : "border-gray-200 bg-white text-[#2d5a3d] hover:border-[#25D366]"
  }`;
const small = "whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors disabled:opacity-50";

function when(iso) {
  return iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "medium" }) : "—";
}

const ModeBox = ({ token }) => {
  const [mode, setMode] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/admin/signup-mode", token).then((data) => setMode(data.mode)).catch((problem) => setError(problem.message));
  }, [token]);
  const change = (next) =>
    apiFetch("/api/admin/signup-mode", token, { method: "PUT", body: JSON.stringify({ mode: next }) })
      .then((data) => setMode(data.mode))
      .catch((problem) => setError(problem.message));
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 sm:p-7" data-testid="admin-signup-mode">
      <h2 className="font-display text-xl font-bold text-[#122118]">Mode d’inscription</h2>
      <p className="mt-1 text-sm text-[#4a5f53]">
        Le mode Sur invitation garde chaque nouvelle inscription en attente. Approuver crée le compte et envoie le courriel de confirmation. Le mode Ouvert permet à quiconque de s’inscrire directement.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {MODES.map(([id, label]) => (
          <button key={id} type="button" onClick={() => change(id)} className={pill(mode === id)} data-testid={`admin-mode-${id}`}>
            {label}
          </button>
        ))}
        <span className="text-sm text-[#4a5f53]" data-testid="admin-mode-current">
          Mode actuel : {mode === "open" ? "Ouvert" : mode ? "Sur invitation" : "…"}
        </span>
      </div>
      {error && <p className="mt-3 text-sm text-[#9b3b3b]">{error}</p>}
    </section>
  );
};

const Actions = ({ row, busy, act }) => (
  <div className="flex justify-end gap-2">
    {row.status !== "approved" && (
      <button type="button" disabled={busy} onClick={() => act(row, "approve")} className={`${small} border-[#25D366] bg-[#25D366] text-[#0a2914]`} data-testid={`admin-wait-approve-${row.id}`}>
        Approuver
      </button>
    )}
    {row.status === "approved" && !row.usedAt && (
      <button type="button" disabled={busy} onClick={() => act(row, "approve")} className={`${small} border-gray-200 text-[#122118] hover:border-[#25D366]`} data-testid={`admin-wait-resend-${row.id}`}>
        Renvoyer le lien
      </button>
    )}
    {row.status !== "rejected" && !row.usedAt && (
      <button type="button" disabled={busy} onClick={() => act(row, "reject")} className={`${small} border-[#f1c7c2] text-[#c0392b] hover:bg-[#FDF2F1]`} data-testid={`admin-wait-reject-${row.id}`}>
        Rejeter
      </button>
    )}
    {row.usedAt && <span className="text-xs font-semibold text-[#128a3e]">Compte créé</span>}
  </div>
);

export const Waitlist = ({ token }) => {
  const [view, setView] = useState("pending");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ text: "", link: "" });
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    apiFetch(`/api/admin/waitlist?status=${view}`, token).then(setData).catch((problem) => setError(problem.message));
  }, [token, view]);
  useEffect(() => load(), [load]);

  const act = async (row, action) => {
    setBusy(true);
    setError("");
    setNotice({ text: "", link: "" });
    try {
      const result = await apiFetch(`/api/admin/waitlist/${row.id}/${action}`, token, {
        method: "POST",
        body: JSON.stringify({ appUrl: window.location.origin }),
      });
      if (action === "reject") setNotice({ text: `Demande de ${row.name} rejetée.`, link: "" });
      else if (result.delivered) setNotice({ text: `Accès approuvé : courriel de confirmation envoyé à ${row.email}.`, link: "" });
      else if (result.invitePath) setNotice({ text: "Accès approuvé. Mode local, aucun courriel envoyé. Lien :", link: `${window.location.origin}${result.invitePath}` });
      else setNotice({ text: result.error || "Accès approuvé, mais le courriel n’a pas pu partir.", link: "" });
      load();
    } catch (problem) {
      setError(problem.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.requests || [];
  return (
    <div className="space-y-5" data-testid="admin-waitlist">
      <ModeBox token={token} />
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setView(id)} className={pill(view === id)} data-testid={`admin-wait-view-${id}`}>
            {label}
            {data?.counts ? ` (${data.counts[id]})` : ""}
          </button>
        ))}
        <button type="button" onClick={load} className={`${pill(false)} ml-auto inline-flex items-center gap-1.5`} data-testid="admin-wait-refresh">
          <RefreshCw className="h-4 w-4" /> Actualiser
        </button>
      </div>
      {notice.text && (
        <div className="rounded-xl bg-[#E9FBF0] px-4 py-2.5 text-sm text-[#128a3e]" data-testid="admin-wait-notice">
          {notice.text}
          {notice.link && (
            <a href={notice.link} className="mt-1 block break-all underline" data-testid="admin-wait-link">{notice.link}</a>
          )}
        </div>
      )}
      {error && <p className="text-sm text-[#9b3b3b]" data-testid="admin-wait-error">{error}</p>}
      <section className="rounded-3xl border border-gray-100 bg-white p-5 sm:p-7">
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm" data-testid="admin-wait-table">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">
                <th className="py-3 pr-4">Nom</th>
                <th className="py-3 pr-4">Courriel</th>
                <th className="py-3 pr-4">Rôle</th>
                <th className="py-3 pr-4">Note</th>
                <th className="py-3 pr-4">Demandée</th>
                <th className="py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-gray-50 last:border-0" data-testid={`admin-wait-row-${row.id}`}>
                  <td className="py-3.5 pr-4 font-semibold text-[#122118]">{row.name}</td>
                  <td className="py-3.5 pr-4 text-[#4a5f53]">{row.email}</td>
                  <td className="py-3.5 pr-4 text-[#4a5f53]">{ROLE[row.role] || row.role}</td>
                  <td className="max-w-[16rem] py-3.5 pr-4 text-[#4a5f53]">{row.note || "—"}</td>
                  <td className="whitespace-nowrap py-3.5 pr-4 text-xs text-[#4a5f53]">{when(row.createdAt)}</td>
                  <td className="py-3.5"><Actions row={row} busy={busy} act={act} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="space-y-3 md:hidden">
          {rows.map((row) => (
            <li key={row.id} className="rounded-2xl border border-gray-100 p-4" data-testid={`admin-wait-card-${row.id}`}>
              <p className="font-semibold text-[#122118]">{row.name} · <span className="font-normal text-[#4a5f53]">{ROLE[row.role] || row.role}</span></p>
              <p className="truncate text-sm text-[#4a5f53]">{row.email}</p>
              {row.note && <p className="mt-1 text-sm text-[#4a5f53]">{row.note}</p>}
              <p className="mt-1 text-xs text-[#829C8D]">{when(row.createdAt)}</p>
              <div className="mt-3"><Actions row={row} busy={busy} act={act} /></div>
            </li>
          ))}
        </ul>
        {data && !rows.length && <p className="py-6 text-center text-sm text-[#829C8D]" data-testid="admin-wait-empty">Aucune demande.</p>}
        {!data && !error && <p className="py-6 text-center text-sm text-[#829C8D]">Chargement…</p>}
      </section>
    </div>
  );
};
